import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { NodeViewBase } from '@portal-core/text-editor/node-views/node-view.base';
import { GetPosForNodeView } from '@portal-core/text-editor/node-views/types/nodeview-get-pos.type';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { ProseMirrorNode } from 'prosemirror-model';
import { Decoration, EditorView, NodeView } from 'prosemirror-view';

export class PopupNodeView extends NodeViewBase implements NodeView {
  private beforeToggleListener: (event: ToggleEvent) => void;
  private buttonDOM: HTMLButtonElement;
  private iconDOM: HTMLSpanElement;

  constructor(
    node: ProseMirrorNode,
    editorView: EditorView,
    getPos: GetPosForNodeView,
    decorations: Decoration[],
    nodeViewBridge: BidiBridge<EditorEvent, EditorEvent>
  ) {
    super(node, editorView, getPos, decorations, nodeViewBridge);

    // Create the DOM elements
    this.contentDOM = this.dom.appendChild(document.createElement('mc-lwe-popup-content'));
    this.buttonDOM = this.dom.appendChild(document.createElement('button'));
    this.buttonDOM.setAttribute('contenteditable', 'false');
    this.buttonDOM.setAttribute('draggable', 'false');
    this.buttonDOM.setAttribute('type', 'button');
    this.buttonDOM.classList.add('mc-lwe-popup-button');
    this.iconDOM = this.buttonDOM.appendChild(document.createElement('span'));
    this.iconDOM.setAttribute('draggable', 'false');
    this.iconDOM.classList.add('icon-down-arrow-90', 'mc-expansion-button-indicator');

    // Listen for beforetoggle events to sync the node open state
    this.beforeToggleListener = (event) => {
      if (this.dom.contains(event.target as HTMLElement)) {
        if ((event as any).newState === 'open') {
          this.dom.setAttribute('mc-lwe-popup-open', '');
        } else {
          this.dom.removeAttribute('mc-lwe-popup-open');
        }

        this.syncNode();
      }
    };

    // The beforetoggle event does not bubble so use the capture phase
    this.dom.addEventListener('beforetoggle', this.beforeToggleListener, { capture: true });

    this.addArrowKeyNavigation(this.buttonDOM);

    // Sync the node
    this.syncNode();

    // Sync the node again once the popup head and body are ready
    this.syncNodeWhenReady(() => {
      return !!this.contentDOM.querySelector<HTMLElement>('MadCap\\:popupHead') && !!this.contentDOM.querySelector<HTMLElement>('MadCap\\:popupBody');
    });
  }

  destroy() {
    this.dom.removeEventListener('beforetoggle', this.beforeToggleListener, { capture: true });
    super.destroy();
  }

  ignoreMutation(mutation: MutationRecord): boolean {
    // Ignore toggling the popup open attribute
    if (mutation.type === 'attributes' && mutation.target === this.dom && mutation.attributeName === 'mc-lwe-popup-open') {
      return true;
    }

    return super.ignoreMutation(mutation);
  }

  protected getArrowKeyTargetPos(pos: number, dir: -1 | 1, element: HTMLElement): number {
    const firstChild = this.node.firstChild;

    if (dir < 0) {
      // Navigate to the end of the popup head (first child)
      return firstChild ? pos + 1 + firstChild.nodeSize - 1 : pos;
    }

    // If the popup body is open, navigate to the start of the popup body (after first child)
    const isOpen = this.dom.hasAttribute('mc-lwe-popup-open');
    if (isOpen && firstChild) {
      return pos + 1 + firstChild.nodeSize;
    }

    // Body is closed - navigate past the entire popup
    return pos + this.node.nodeSize;
  }

  stopEvent(event: Event): boolean {
    return this.buttonDOM?.contains(event.target as Node);
  }

  syncNode() {
    // Use requestAnimationFrame to ensure this runs after the DOM has updated.
    // This is necessary for some cases such as the popup node being deleted while tracked changes are enabled.
    // That causes the popup body to re-render as a new element after syncNode is called. To use the new popup body, requestAnimationFrame is used.
    requestAnimationFrame(() => {
      const popupHeadDOM = this.contentDOM.querySelector<HTMLElement>('MadCap\\:popupHead');
      const popupBodyDOM = this.contentDOM.querySelector<HTMLElement>('MadCap\\:popupBody');
      const isOpen = this.dom.hasAttribute('mc-lwe-popup-open');

      this.buttonDOM.classList.toggle('mc-expansion-button-expanded', isOpen);

      if (popupHeadDOM && popupBodyDOM) {
        popupBodyDOM.setAttribute('contenteditable', isOpen ? 'true' : 'false');
        (this.buttonDOM as any).popoverTargetElement = popupBodyDOM;
        (this.buttonDOM as any).popoverTargetAction = 'toggle';
      }
    });
  }
}
